/**
 * The access token from the stored login, or undefined when nobody is signed
 * in. A malformed entry is treated as signed out rather than thrown, because a
 * parse error here used to take down every caller including the layout that
 * decides which navigation to render.
 */
export const getToken = (): string | undefined => {
  const storedLoginUser = localStorage.getItem('login');
  if (!storedLoginUser) {
    return undefined;
  }
  try {
    return JSON.parse(storedLoginUser).access;
  } catch (error) {
    return undefined;
  }
};

/** Whether a session is stored. Used to decide what the public header shows. */
export const isSignedIn = (): boolean => Boolean(getToken());

/**
 * Name of the window event fired when the account flags beside the tokens
 * change, so pages that show staff only controls can redraw without a reload.
 */
export const LOGIN_FLAGS_EVENT = 'login-flags-changed';

/** The stored login as an object, or null when nobody is signed in. */
export const readLogin = (): Record<string, unknown> | null => {
  const stored = localStorage.getItem('login');
  if (!stored) {
    return null;
  }
  try {
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

/**
 * The stored tokens, read at the moment of use, or null when there is no
 * complete session. Sign out sends both: the access token to authenticate and
 * the refresh token for the server to revoke. Until sprint 17 sign out used a
 * copy of the login taken when the page first loaded, which is null after a
 * login in the same tab (and the previous account's after a switch), so no
 * logout request went out and the refresh token stayed valid.
 */
export const readSessionTokens = (): { access: string; refresh: string } | null => {
  const login = readLogin();
  if (!login) {
    return null;
  }
  const { access, refresh } = login;
  if (typeof access !== 'string' || !access || typeof refresh !== 'string' || !refresh) {
    return null;
  }
  return { access, refresh };
};

/**
 * Keep the account's staff flag beside the tokens (sprint 17). The login call
 * answers with the two tokens only, so the flag is read from the user info
 * endpoint and merged in here. The tokens are left exactly as they are.
 *
 * Nothing is written unless the session that asked is still the one stored,
 * so a slow answer cannot bring back a session that was signed out or give
 * one account's flag to another that signed in meanwhile.
 *
 * This flag only decides what the page offers. The server checks is_staff on
 * every change, whatever the browser holds.
 */
export const storeSessionFlags = (
  username: string,
  isStaff: boolean,
  forAccess: string,
): boolean => {
  if (typeof username !== 'string' || typeof isStaff !== 'boolean') {
    return false;
  }
  if (!forAccess) {
    return false;
  }
  const login = readLogin();
  if (!login || login.access !== forAccess) {
    return false;
  }
  localStorage.setItem(
    'login',
    JSON.stringify({ ...login, username, is_staff: isStaff }),
  );
  window.dispatchEvent(new Event(LOGIN_FLAGS_EVENT));
  return true;
};
